import { describeApiError, type RoomDirectory } from '@brush/api-client';
import { ErrorNotice, Spinner } from '@brush/ui';
import { ArrowLeft, CreditCard, Plus } from 'lucide-react';
import { useState } from 'react';
import { useScreenViewed, useSession } from '../api';
import { methodLabel } from '../claim';
import { revokedCardNotice } from '../membership';
import { usePlayer } from '../player';
import { useRooms, useUnlinkMembership } from '../queries';
import { STAFF_AS_PLAYER_NOTICE } from '../roles';
import { themeFor } from '../theme';
import { CenteredScreen } from './CenteredScreen';
import { ConnectCard } from './ConnectCard';
import { EraseAccount } from './EraseAccount';
import { RoleSwitch } from './RoleSwitch';

const LINKED_ON = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/**
 * Which card am I — asked with zero, one or several answers (ADR 0010, Decision 6).
 *
 * <b>One screen for all three states, deliberately.</b> They read as different screens — a room
 * directory, a switcher, a list — and they are one question, so splitting them would mean three
 * places that have to agree about what connecting a card is called and where it starts from. The
 * zero case is a *first-class screen and not an error*: it retires the "This account isn't linked
 * to a player" dead end, which told somebody to ask their room to do something no operator can do
 * (Decision 8).
 *
 * It is also the landing screen for exactly two of those states, and the App decides which:
 * nothing connected, or more than one connected with no preference stored. Holding a single card
 * never lands here — one card is not a choice — and the portal chrome is how somebody with one
 * gets back to it.
 */
export function CardsScreen({ onClose }: { onClose?: (() => void) | null }) {
  const { memberships, selectRoom, forgetRoom, revokedCard } = usePlayer();
  useScreenViewed('cards', null);
  const roomsQuery = useRooms();
  const unlink = useUnlinkMembership();
  const { signOut } = useSession();
  const theme = themeFor(null);

  const [connecting, setConnecting] = useState<{ roomId: string | null } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const rooms: readonly RoomDirectory[] = roomsQuery.data ?? [];

  if (connecting) {
    return (
      <ConnectCard
        rooms={rooms}
        initialRoomId={connecting.roomId}
        onConnected={(roomId) => {
          selectRoom(roomId);
          setConnecting(null);
          onClose?.();
        }}
        onCancel={() => setConnecting(null)}
      />
    );
  }

  const panel = { background: theme.panel, border: `1px solid ${theme.border}` };
  const row = { background: theme.panelLight };

  const disconnect = (roomId: string) => {
    unlink.mutate(roomId, {
      onSuccess: () => {
        setConfirming(null);
        // The preference named a room this account no longer holds a card at. Resolution already
        // falls back on its own; clearing it stops a stale id outliving the card by years.
        forgetRoom(roomId);
      },
    });
  };

  return (
    <CenteredScreen background={theme.bg} color={theme.text}>
      <div className="w-full max-w-lg rounded-2xl p-6" style={panel}>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="mb-4 flex items-center gap-1.5 text-xs"
            style={{ color: theme.textMuted }}
          >
            <ArrowLeft className="size-3.5" /> Back to my portal
          </button>
        ) : null}

        {/* CR4. Above the heading because it is the answer to "why am I looking at this screen" —
            somebody was reading a leaderboard a moment ago and the portal closed under them, and
            the heading below is the consequence rather than the explanation.

            Chalk, never signal orange, and never `ErrorNotice`. A room withdrawing a card is a
            decision somebody took on purpose, not a fault in the app; styling it as one would send
            a player to support over something working exactly as intended. Same call the fantasy
            amended-after-lock marker already makes, and in this app orange means "this is you" and
            is never a control. */}
        {revokedCard ? (
          <section
            role="status"
            className="mb-5 rounded-lg p-4"
            style={{ background: theme.panelLight, border: `1px solid ${theme.border}` }}
          >
            <h2 className="text-xs font-bold uppercase tracking-wider">Card disconnected</h2>
            <p className="mt-1.5 text-xs leading-relaxed" style={{ color: theme.textMuted }}>
              {revokedCardNotice(revokedCard)}
            </p>
          </section>
        ) : null}

        <h1 className="text-lg font-bold">
          {memberships.length > 0 ? 'Your cards' : 'Connect your player card'}
        </h1>

        {memberships.length > 0 ? (
          <>
            <p className="mt-1 text-sm" style={{ color: theme.textMuted }}>
              Choose which room&apos;s portal to open. You can hold one card at each room.
            </p>

            <ul className="mt-5 space-y-2">
              {memberships.map((m) => (
                <li key={m.roomId} className="rounded-lg p-3" style={row}>
                  <div className="flex items-start gap-3">
                    <span
                      className="flex size-9 shrink-0 items-center justify-center rounded-full"
                      style={{ background: theme.primary, color: theme.text }}
                    >
                      <CreditCard className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{m.roomName}</span>
                      <span className="block text-xs" style={{ color: theme.textMuted }}>
                        Card {m.cardId} · {m.displayName}
                      </span>
                      <span className="block text-[11px]" style={{ color: theme.textMuted }}>
                        {methodLabel(m.method)} · connected{' '}
                        {LINKED_ON.format(new Date(m.linkedAt))}
                      </span>
                    </span>
                  </div>

                  {confirming === m.roomId ? (
                    <div className="mt-3">
                      <p className="text-xs" style={{ color: theme.textMuted }}>
                        Disconnect this card? Your results and standings at {m.roomName} stay
                        exactly as they are — the card is theirs, not this sign-in&apos;s. You can
                        connect it again with a new code.
                      </p>
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => disconnect(m.roomId)}
                          disabled={unlink.isPending}
                          className="rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                          style={{ border: `1px solid ${theme.border}`, color: theme.text }}
                        >
                          {unlink.isPending ? 'Disconnecting…' : 'Disconnect'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirming(null)}
                          className="rounded-lg px-3 py-1.5 text-xs"
                          style={{ color: theme.textMuted }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          selectRoom(m.roomId);
                          onClose?.();
                        }}
                        className="flex-1 rounded-lg py-2 text-xs font-semibold"
                        style={{ background: theme.accent, color: theme.bg }}
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          unlink.reset();
                          setConfirming(m.roomId);
                        }}
                        className="rounded-lg px-3 py-2 text-xs"
                        style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
                      >
                        Disconnect
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <ErrorNotice message={describeApiError(unlink.error)} className="mt-4" />

            <button
              type="button"
              onClick={() => setConnecting({ roomId: null })}
              className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm"
              style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
            >
              <Plus className="size-4" /> Connect a card at another room
            </button>
          </>
        ) : (
          <>
            <p className="mt-1 text-sm" style={{ color: theme.textMuted }}>
              Your sign-in isn&apos;t connected to a player card yet. Pick the room that issued
              yours and prove it is you with a code sent to the address they hold for it.
            </p>

            <div className="mt-5 max-h-72 overflow-auto">
              {roomsQuery.isLoading ? (
                <div className="py-6">
                  <Spinner label="Loading rooms…" />
                </div>
              ) : rooms.length === 0 ? (
                <p className="py-6 text-center text-sm" style={{ color: theme.textMuted }}>
                  No rooms are listed yet.
                </p>
              ) : (
                <ul className="space-y-1">
                  {rooms.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => setConnecting({ roomId: r.id })}
                        className="flex w-full items-center gap-3 rounded-lg p-3 text-left transition hover:opacity-90"
                        style={row}
                      >
                        <span
                          className="flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                          style={{ background: r.primaryColor, color: theme.text }}
                        >
                          {r.logo}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold">{r.name}</span>
                          <span className="block text-xs" style={{ color: theme.textMuted }}>
                            {r.location}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <p className="mt-4 text-[11px]" style={{ color: theme.textMuted }}>
              Can&apos;t find your room, or the room has no way to reach you? Ask the desk to check
              the contact details on your card — correcting those is the only lever they have here,
              and nobody can connect a card on your behalf.
            </p>
          </>
        )}

        <RoleSwitch notice={memberships.length === 0 ? STAFF_AS_PLAYER_NOTICE : null} />

        <EraseAccount cards={memberships.map(({ roomName, cardId }) => ({ roomName, cardId }))} theme={theme} />

        {signOut ? (
          <button
            type="button"
            onClick={signOut}
            className="mt-4 w-full rounded-lg py-2 text-xs"
            style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
          >
            Sign out
          </button>
        ) : null}
      </div>
    </CenteredScreen>
  );
}
